import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Modal from '../../components/Modal';
import { deleteDriver, listDrivers, upsertDriver, type DriverRecord } from '../../services/drivers';

const driverSchema = z.object({
  fullName: z.string().min(2, 'Ad zorunludur'),
  phone: z.string().min(6, 'Telefon zorunludur'),
  vehicleInfo: z.string().optional(),
  isOnline: z.boolean().default(false),
  pushToken: z.string().optional(),
});

type DriverFormValues = z.infer<typeof driverSchema>;

const DEFAULT_VALUES: DriverFormValues = {
  fullName: '',
  phone: '',
  vehicleInfo: '',
  isOnline: false,
  pushToken: '',
};

export default function DriverManager() {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDriver, setEditingDriver] = useState<DriverRecord | null>(null);

  const {
    data: drivers = [],
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['people', 'drivers'],
    queryFn: listDrivers,
  });

  const form = useForm<DriverFormValues>({
    resolver: zodResolver(driverSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingDriver(null);
    form.reset(DEFAULT_VALUES);
  }, [form]);

  const openCreate = () => {
    setEditingDriver(null);
    form.reset(DEFAULT_VALUES);
    setModalOpen(true);
  };

  const openEdit = (driver: DriverRecord) => {
    setEditingDriver(driver);
    form.reset({
      fullName: driver.fullName ?? '',
      phone: driver.phone ?? '',
      vehicleInfo: driver.vehicleInfo ?? '',
      isOnline: driver.isOnline,
      pushToken: driver.pushToken ?? '',
    });
    setModalOpen(true);
  };

  const upsertMutation = useMutation({
    mutationFn: upsertDriver,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['people', 'drivers'] });
      closeModal();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (driver: DriverRecord) => deleteDriver(driver.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['people', 'drivers'] });
    },
  });

  const onSubmit = async (values: DriverFormValues) => {
    await upsertMutation.mutateAsync({
      id: editingDriver?.id,
      fullName: values.fullName,
      phone: values.phone,
      vehicleInfo: values.vehicleInfo,
      isOnline: values.isOnline,
      pushToken: values.pushToken,
    });
  };

  return (
    <>
      <div className="card">
        <div className="card-header">
          <div>
            <p className="card-title">Kurye listesi</p>
            <p className="card-subtitle">
              {isLoading || isFetching ? 'Kurye durumu güncelleniyor...' : `${drivers.length} kurye`}
            </p>
          </div>
          <button type="button" className="button primary" onClick={openCreate}>
            + Kurye Ekle
          </button>
        </div>

        {drivers.length === 0 ? (
          <div className="empty-state">
            <strong>Henüz kurye tanımlanmadı</strong>
            <p>Teslimat performansını izlemek için kuryeleri kaydedin.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Ad</th>
                  <th>İletişim</th>
                  <th>Araç</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {drivers.map((driver) => (
                  <tr key={driver.id}>
                    <td>{driver.fullName ?? '—'}</td>
                    <td>
                      <div className="helper-text">
                        <div>{driver.phone ?? '—'}</div>
                        <div>{driver.pushToken ? 'Push hazır' : 'Token yok'}</div>
                      </div>
                    </td>
                    <td>{driver.vehicleInfo ?? '—'}</td>
                    <td>
                      <span className={`chip ${driver.isOnline ? 'success' : 'neutral'}`}>
                        {driver.isOnline ? 'Çevrimiçi' : 'Çevrimdışı'}
                      </span>
                      {driver.lastSeenAt ? (
                        <p className="helper-text">
                          Son görülen:{' '}
                          {new Date(driver.lastSeenAt).toLocaleString('tr-TR', {
                            timeZone: 'Europe/Istanbul',
                          })}
                        </p>
                      ) : null}
                    </td>
                    <td>
                      <div className="table-actions">
                        <button type="button" className="button secondary" onClick={() => openEdit(driver)}>
                          Düzenle
                        </button>
                        <button
                          type="button"
                          className="button danger"
                          onClick={() => {
                            if (window.confirm(`${driver.fullName ?? driver.id} kaydını silmek istediğinize emin misiniz?`)) {
                              deleteMutation.mutate(driver);
                            }
                          }}
                        >
                          Sil
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editingDriver ? `${editingDriver.fullName ?? editingDriver.id} düzenleniyor` : 'Kurye kaydet'}
      >
        <form className="form-grid" onSubmit={form.handleSubmit(onSubmit)}>
          <label className="form-field">
            <span>Ad soyad</span>
            <input type="text" {...form.register('fullName')} />
            {form.formState.errors.fullName ? (
              <span className="error-text">{form.formState.errors.fullName.message}</span>
            ) : null}
          </label>

          <label className="form-field">
            <span>Telefon</span>
            <input type="tel" {...form.register('phone')} />
            {form.formState.errors.phone ? (
              <span className="error-text">{form.formState.errors.phone.message}</span>
            ) : null}
          </label>

          <label className="form-field">
            <span>Araç bilgisi</span>
            <input type="text" placeholder="Mavi motor, 34 ABC 123" {...form.register('vehicleInfo')} />
          </label>

          <label className="form-field">
            <span>Expo push token (opsiyonel)</span>
            <textarea rows={2} {...form.register('pushToken')} />
            <span className="helper-text">Kurye tokenını manuel olarak buraya yapıştırabilirsiniz.</span>
          </label>

          <label className="form-field checkbox inline-checkbox">
            <input type="checkbox" {...form.register('isOnline')} />
            <span>Kuryeyi çevrimiçi işaretle</span>
          </label>

          <div className="form-actions">
            <button type="button" className="button ghost" onClick={closeModal}>
              İptal
            </button>
            <button type="submit" className="button primary" disabled={upsertMutation.isPending}>
              {editingDriver ? 'Kuryeyi kaydet' : 'Kurye ekle'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
